import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import '../../Css/tables/tabla_consultar.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import { registrarEntidad, obtenerEntidades, actualizarEntidad, eliminarEntidad } from '../../Services/serviciohttp';

interface CatalogoPProps {
    title: string;
    apiUrls: {
        registrar: string;
        obtener: string;
        obtenerPorCve: (cve: string) => string;
        actualizar: (cve: string) => string;
        eliminar: (cve: string) => string;
    };
}

interface RegistroItem {
    cve: string;
    nombre: string;
    id: string | number;
}

const CatalogoP: React.FC<CatalogoPProps> = ({ title, apiUrls }) => {
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [clave, setClave] = useState('');
    const [id, setId] = useState('');
    const [nombre, setNombre] = useState('');
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [data, setData] = useState<RegistroItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const toast = useRef<Toast>(null);

    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const searchTermLower = searchTerm.toLowerCase();
        return data.filter(item =>
            String(item.cve).toLowerCase().includes(searchTermLower) ||
            String(item.nombre).toLowerCase().includes(searchTermLower) ||
            String(item.id).toLowerCase().includes(searchTermLower)
        );
    }, [data, searchTerm]);

    useEffect(() => {
        if (mostrarTabla) {
            obtenerRegistros();
        }
    }, [mostrarTabla]);

    const obtenerRegistros = async () => {
        try {
            const result = await obtenerEntidades(apiUrls.obtener);
            setData(result);
        } catch (error) {
            console.error('Error al obtener los registros:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar los datos',
                life: 3000
            });
        }
    };

    const handleNuevo = () => {
        setClave('');
        setNombre('');
        setId('');
        setMode('new');
    };

    const handleEditar = () => {
        if (!clave) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un registro para editar',
                life: 3000
            });
            return;
        }
        setMode('edit');
    };

    const handleGuardar = async () => {
        try {
            if (!clave || !nombre) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Campos requeridos',
                    detail: 'Debe completar todos los campos obligatorios',
                    life: 3000
                });
                return;
            }

            if (mode === 'new') {
                await registrarEntidad(apiUrls.registrar, clave, nombre);
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Éxito!',
                    detail: 'Registro creado exitosamente',
                    life: 3000
                });
            } else if (mode === 'edit') {
                await actualizarEntidad(apiUrls.actualizar(clave), nombre);
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Actualizado!',
                    detail: 'Registro modificado exitosamente',
                    life: 3000
                });
            }

            setClave('');
            setNombre('');
            setId('');
            setMode('view');
            obtenerRegistros();
        } catch (error) {
            console.error('Error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar el registro',
                life: 3000
            });
        }
    };

    const handleEliminar = async () => {
        if (!clave) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un registro para eliminar',
                life: 3000
            });
            return;
        }

        try {
            const confirmacion = window.confirm('⚠️ ¿Estás seguro de que deseas eliminar este registro?\n\nEsta acción no se puede deshacer.');
            if (!confirmacion) return;

            await eliminarEntidad(apiUrls.eliminar(clave));

            toast.current?.show({
                severity: 'success',
                summary: '¡Eliminado!',
                detail: 'Registro eliminado exitosamente',
                life: 3000
            });

            setClave('');
            setNombre('');
            setId('');
            setMode('view');
            obtenerRegistros();
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar el registro',
                life: 3000
            });
        }
    };

    const handleCancelar = () => {
        setClave('');
        setNombre('');
        setId('');
        setMode('view');
    };

    const handleConsultar = () => {
        setMostrarTabla(true);
    };

    const cerrarTabla = () => {
        setMostrarTabla(false);
        setSearchTerm('');
    };

    const handleSeleccionarRegistro = (registro: RegistroItem) => {
        setClave(registro.cve);
        setNombre(registro.nombre);
        setId(String(registro.id));
        setMostrarTabla(false);
        setMode('view');
        setSearchTerm('');
    };

    const getModeIndicator = () => {
        switch (mode) {
            case 'new':
                return { text: 'NUEVO REGISTRO', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO REGISTRO', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    return (
        <div className="content-wrapper" style={{
            backgroundImage: 'url(/dist/img/FondoLogin.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            minHeight: '100vh',
            position: 'relative',
            padding: '20px'
        }}>
            
            <Toast ref={toast} />

            {/* Modal de la tabla mejorado con scroll arreglado */}
            {mostrarTabla && (
                <div className="overlay" onClick={cerrarTabla} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        {/* Header fijo */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                📋 Consultar Registros
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={cerrarTabla}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        {/* Barra de búsqueda fija */}
                        <div style={{ marginBottom: '20px', flexShrink: 0 }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar por clave, nombre o ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Contador de resultados fijo */}
                        <div style={{ 
                            marginBottom: '15px', 
                            fontSize: '0.9em', 
                            color: '#6b7280',
                            padding: '8px 12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            flexShrink: 0
                        }}>
                            📊 Mostrando <strong>{filteredData.length}</strong> de <strong>{data.length}</strong> registros
                        </div>

                        {/* Contenedor de tabla con scroll */}
                        <div style={{ 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Header de tabla fijo */}
                            <div style={{ 
                                backgroundColor: '#f8fafc',
                                borderBottom: '2px solid #e5e7eb',
                                flexShrink: 0
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '30%'
                                            }}>🔑 Clave</th>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '70%'
                                            }}>📝 Nombre</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                            
                            {/* Cuerpo de tabla con scroll */}
                            <div style={{ 
                                flex: 1,
                                overflowY: 'auto',
                                maxHeight: 'calc(80vh - 200px)'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {filteredData.map((item, index) => (
                                            <tr 
                                                key={index} 
                                                onClick={() => handleSeleccionarRegistro(item)}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                    transition: 'background-color 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                            >
                                                <td style={{ 
                                                    padding: '12px 16px', 
                                                    borderBottom: '1px solid #f3f4f6',
                                                    width: '30%'
                                                }}>{item.cve}</td>
                                                <td style={{ 
                                                    padding: '12px 16px', 
                                                    borderBottom: '1px solid #f3f4f6',
                                                    width: '70%'
                                                }}>{item.nombre}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Formulario principal mejorado */}
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '800px',
                margin: '0 auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
                {/* Header con indicador de modo */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                            {title}
                        </h2>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: modeInfo.color,
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className={`pi ${modeInfo.icon}`}></i>
                            {modeInfo.text}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Formulario */}
                <div style={{ marginBottom: '24px' }}>
                    <form autoComplete="off">
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', alignItems: 'end' }}>
                            {/* Campo Clave */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    fontSize: '0.95rem', 
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    color: '#374151'
                                }}>
                                    🔑 Clave <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        fontSize: '0.95rem',
                                        padding: '0 12px',
                                        border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px',
                                        backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                    }}
                                    value={clave}
                                    onChange={(e) => setClave(e.target.value)}
                                    disabled={mode === 'view' || mode === 'edit'}
                                    placeholder="Ingrese la clave"
                                />
                            </div>

                            {/* Campo Nombre */}
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    fontSize: '0.95rem', 
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    color: '#374151'
                                }}>
                                    📝 Nombre <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        fontSize: '0.95rem',
                                        padding: '0 12px',
                                        border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px',
                                        backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                    }}
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="Ingrese el nombre"
                                />
                            </div>
                        </div>
                    </form>
                </div>

                {/* Botones reorganizados en un solo flujo lógico */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {mode === 'view' ? (
                        <>
                            <Button
                                icon="pi pi-search"
                                label="Consultar"
                                className="p-button-info p-button-raised"
                                onClick={handleConsultar}
                                style={{ 
                                    height: '44px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: '120px'
                                }}
                            />
                            <Button
                                icon="pi pi-plus"
                                label="Nuevo"
                                className="p-button-success p-button-raised"
                                onClick={handleNuevo}
                                style={{ 
                                    height: '44px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: '120px'
                                }}
                            />
                            <Button
                                icon="pi pi-pencil"
                                label="Editar"
                                className="p-button-warning p-button-raised"
                                onClick={handleEditar}
                                disabled={!clave}
                                style={{ 
                                    height: '44px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: '120px',
                                    opacity: !clave ? 0.5 : 1
                                }}
                            />
                            <Button
                                icon="pi pi-trash"
                                label="Eliminar"
                                className="p-button-danger p-button-raised"
                                onClick={handleEliminar}
                                disabled={!clave}
                                style={{ 
                                    height: '44px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: '120px',
                                    opacity: !clave ? 0.5 : 1
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <Button
                                icon="pi pi-check"
                                label="Guardar"
                                className="p-button-success p-button-raised"
                                onClick={handleGuardar}
                                style={{ 
                                    height: '44px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: '120px'
                                }}
                            />
                            <Button
                                icon="pi pi-times"
                                label="Cancelar"
                                className="p-button-secondary p-button-raised"
                                onClick={handleCancelar}
                                style={{ 
                                    height: '44px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: '120px'
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CatalogoP;